import { useState } from "react";
import Papa from "papaparse";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusBadge } from "@/components/StatusBits";
import { api, errMsg } from "@/lib/api";

const TARGETS = ["name", "phone", "business", "email", "notes", "tags", "assigned_agent", "timezone", "status", "callback_at",
  "amount_owed", "approval_amount", "lender", "terms", "missing_documents", "offer_details", "sms_consent"];

const guess = (h) => {
  const k = h.toLowerCase().replace(/[^a-z]/g, "");
  const map = { fullname: "name", contactname: "name", name: "name", phone: "phone", phonenumber: "phone", mobile: "phone", cell: "phone",
    business: "business", company: "business", businessname: "business", email: "email", notes: "notes", tags: "tags", agent: "assigned_agent",
    assignedagent: "assigned_agent", timezone: "timezone", status: "status", callback: "callback_at", callbackat: "callback_at",
    amountowed: "amount_owed", balance: "amount_owed", approvalamount: "approval_amount", lender: "lender", terms: "terms",
    missingdocuments: "missing_documents", offerdetails: "offer_details", smsconsent: "sms_consent" };
  return map[k] || "skip";
};

export const ImportWizard = ({ open, onOpenChange, onDone }) => {
  const [raw, setRaw] = useState(null);
  const [mapping, setMapping] = useState({});
  const [preview, setPreview] = useState(null);
  const [strategy, setStrategy] = useState("skip");
  const [busy, setBusy] = useState(false);
  const reset = () => { setRaw(null); setPreview(null); setMapping({}); };

  const onFile = (file) => Papa.parse(file, {
    header: true, skipEmptyLines: true,
    complete: (res) => {
      setRaw(res);
      setMapping(Object.fromEntries(res.meta.fields.map((h) => [h, guess(h)])));
      setPreview(null);
    },
  });
  const rows = () => raw.data.map((r) => {
    const o = {};
    Object.entries(mapping).forEach(([h, t]) => { if (t !== "skip" && r[h] !== undefined) o[t] = r[h]; });
    return o;
  });
  const doPreview = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/contacts/import/preview", { rows: rows() });
      setPreview(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const commit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/contacts/import/commit", { rows: rows(), duplicate_strategy: strategy });
      toast.success(`Imported ${data.inserted}, updated ${data.updated}, skipped ${data.skipped}. No calls were placed.`);
      reset();
      onOpenChange(false);
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="bg-white max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="import-dialog">
        <DialogHeader><DialogTitle>Import contacts from CSV</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">Importing never places calls and never clears an existing opt-out, do-not-call or attempt history.</p>
        <input type="file" accept=".csv,text/csv" data-testid="import-file-input" onChange={(e) => e.target.files[0] && onFile(e.target.files[0])} className="text-sm" />
        {raw && (
          <div className="space-y-2">
            <p className="text-sm font-medium">Map columns ({raw.data.length} rows)</p>
            <div className="grid sm:grid-cols-3 gap-2">
              {raw.meta.fields.map((h) => (
                <div key={h} className="flex items-center gap-2 text-xs">
                  <span className="w-28 truncate font-medium" title={h}>{h}</span>
                  <Select value={mapping[h]} onValueChange={(v) => { setMapping({ ...mapping, [h]: v }); setPreview(null); }}>
                    <SelectTrigger data-testid={`import-map-${h}`} className="h-8 bg-white text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="skip">Skip</SelectItem>
                      {TARGETS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
            <Button size="sm" data-testid="import-preview-btn" disabled={busy || !Object.values(mapping).includes("phone") || !Object.values(mapping).includes("name")} onClick={doPreview}>Preview</Button>
          </div>
        )}
        {preview && (
          <div className="space-y-2" data-testid="import-preview">
            <p className="text-xs text-slate-600" data-testid="import-summary">
              {preview.summary.valid} valid · {preview.summary.invalid} invalid · {preview.summary.duplicates_in_file} duplicate in file · {preview.summary.existing} already exist
            </p>
            <div className="max-h-72 overflow-auto border rounded-md">
              <Table>
                <TableHeader><TableRow><TableHead>#</TableHead><TableHead>Name</TableHead><TableHead>Phone</TableHead><TableHead>Result</TableHead></TableRow></TableHeader>
                <TableBody>
                  {preview.rows.slice(0, 300).map((r) => (
                    <TableRow key={r.row} className="text-xs">
                      <TableCell className="py-1.5">{r.row}</TableCell><TableCell className="py-1.5">{r.name}</TableCell>
                      <TableCell className="py-1.5 font-mono">{r.phone || r.raw_phone}</TableCell>
                      <TableCell className="py-1.5 space-x-1">
                        {!r.valid && <StatusBadge tone="danger" value="x">{r.issues.join(", ")}</StatusBadge>}
                        {r.duplicate_in_file && <StatusBadge tone="warning" value="x">Duplicate in file</StatusBadge>}
                        {r.duplicate_existing && <StatusBadge tone="info" value="x">Exists: {r.duplicate_existing.name}</StatusBadge>}
                        {r.valid && !r.duplicate_in_file && !r.duplicate_existing && <StatusBadge tone="success" value="x">New</StatusBadge>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs">Existing contacts:</span>
              <Select value={strategy} onValueChange={setStrategy}>
                <SelectTrigger data-testid="import-duplicate-strategy" className="h-8 w-56 bg-white text-xs"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="skip">Skip them</SelectItem>
                  <SelectItem value="update">Update blank details (keeps opt-outs)</SelectItem>
                </SelectContent>
              </Select>
              <Button size="sm" className="ml-auto" data-testid="import-commit-btn" disabled={busy} onClick={commit}>Import</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
